/**
 * Toggle Component
 * Styled toggle switch with labels and variants
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { spacing, typography, transitions, borderRadius } from '../tokens';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
}

const getSizeStyles = (size: string) => {
  switch (size) {
    case 'sm':
      return {
        width: '36px',
        height: '20px',
        thumbSize: '14px',
        thumbOffset: '3px',
        thumbTranslate: '16px',
      };
    case 'lg':
      return {
        width: '56px',
        height: '30px',
        thumbSize: '24px',
        thumbOffset: '3px',
        thumbTranslate: '26px',
      };
    default:
      return {
        width: '44px',
        height: '24px',
        thumbSize: '18px',
        thumbOffset: '3px',
        thumbTranslate: '20px',
      };
  }
};

const ToggleWrapper = styled.label<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing['3']};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`;

const ToggleContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToggleLabel = styled.span`
  display: block;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  line-height: 1.4;
`;

const ToggleDescription = styled.span`
  display: block;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  margin-top: ${spacing['1']};
  line-height: 1.4;
`;

const ToggleTrack = styled.div<{
  $checked: boolean;
  $size: string;
  $variant: string;
  $disabled: boolean;
}>`
  position: relative;
  width: ${(props) => getSizeStyles(props.$size).width};
  height: ${(props) => getSizeStyles(props.$size).height};
  background: ${(props) => {
    if (props.$checked) {
      switch (props.$variant) {
        case 'success':
          return props.theme?.colors?.accent?.success || '#41A35F';
        case 'warning':
          return props.theme?.colors?.accent?.warning || '#f59e0b';
        case 'danger':
          return props.theme?.colors?.accent?.danger || '#ef4444';
        default:
          return props.theme?.colors?.accent?.primary || '#32828B';
      }
    }
    return props.theme?.colors?.input?.border || 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: ${borderRadius.full};
  transition: ${transitions.all};
  flex-shrink: 0;

  &:hover {
    ${(props) =>
      !props.$disabled &&
      css`
        background: ${props.$checked
          ? props.theme?.colors?.accent?.primary || '#2d757d'
          : props.theme?.colors?.input?.hoverBackground || 'rgba(255, 255, 255, 0.3)'};
      `}
  }
`;

const ToggleThumb = styled.div<{
  $checked: boolean;
  $size: string;
}>`
  position: absolute;
  top: ${(props) => getSizeStyles(props.$size).thumbOffset};
  left: ${(props) => getSizeStyles(props.$size).thumbOffset};
  width: ${(props) => getSizeStyles(props.$size).thumbSize};
  height: ${(props) => getSizeStyles(props.$size).thumbSize};
  background: #ffffff;
  border-radius: ${borderRadius.full};
  transition: ${transitions.all};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  ${(props) =>
    props.$checked &&
    css`
      transform: translateX(${getSizeStyles(props.$size).thumbTranslate});
    `}
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  variant = 'default',
  className,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onChange(!checked);
      }
    }
  };

  return (
    <ToggleWrapper $disabled={disabled} className={className}>
      <ToggleTrack
        $checked={checked}
        $size={size}
        $variant={variant}
        $disabled={disabled}
        role="switch"
        aria-checked={checked}
        // The wrapper is a <label>, so its implicit association goes to the
        // hidden checkbox below -- not to this element, which is what actually
        // takes focus. Without this the switch is announced with no name at all.
        aria-label={label}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        <ToggleThumb $checked={checked} $size={size} />
        <HiddenInput
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          tabIndex={-1}
        />
      </ToggleTrack>
      {(label || description) && (
        <ToggleContent>
          {label && <ToggleLabel>{label}</ToggleLabel>}
          {description && <ToggleDescription>{description}</ToggleDescription>}
        </ToggleContent>
      )}
    </ToggleWrapper>
  );
};

export default Toggle;
